import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, CheckCircle2, Clock, AlertTriangle, ExternalLink, ImageIcon, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { CITIES_ALL, PROPERTY_TYPES, PROPERTY_STATUS } from "@/lib/brand";
import { ImageUploader, GalleryUploader } from "@/components/ImageUploaders";
import { Panel, Badge, EmptyState } from "./BuyerPortal";


const LISTING_TYPES = [
    { k: "sale",    l: "Sale (Buy)" },
    { k: "rent",    l: "Rent" },
];

/**
 * Channel Partner "My Listings" — full CRUD for properties the CP submits.
 * Submissions land in `pending` state and are visible on the public site only
 * after an admin approves them via /portal/admin (Partner Submissions panel).
 */
export default function PartnerListings() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // property being edited (or {} for new)

    const load = () => {
        setLoading(true);
        api.get("/partner/properties")
            .then((r) => setItems(r.data.items || []))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
        try { await api.delete(`/partner/properties/${p.id}`); toast.success("Listing deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const counts = useMemo(() => {
        const c = { pending: 0, approved: 0, rejected: 0 };
        items.forEach((p) => { c[p.approval_status || "pending"] = (c[p.approval_status || "pending"] || 0) + 1; });
        return c;
    }, [items]);

    if (editing) {
        return (
            <PartnerPropertyForm
                initial={editing.id ? editing : null}
                onCancel={() => setEditing(null)}
                onSaved={() => { setEditing(null); load(); }}
            />
        );
    }

    return (
        <div className="space-y-6" data-testid="partner-my-listings">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <h1 className="font-serif text-3xl md:text-4xl">My Property Listings</h1>
                    <p className="text-black/55 text-sm mt-2 max-w-2xl">
                        Add properties from your inventory. Each submission is reviewed by our team &mdash;
                        once approved it appears on <b>ebgrealty.in</b> with your partner branding.
                    </p>
                </div>
                <button
                    data-testid="partner-add-listing-btn"
                    onClick={() => setEditing({})}
                    className="inline-flex items-center gap-2 bg-[var(--ebg-gold)] text-black px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] transition-colors shrink-0"
                >
                    <Plus size={15}/> Add New Property
                </button>
            </div>

            {/* Status pills */}
            <div className="grid grid-cols-3 gap-3">
                <StatusChip icon={Clock}         tone="gold"  label="Pending Review" count={counts.pending  || 0} testid="listing-count-pending"/>
                <StatusChip icon={CheckCircle2}  tone="green" label="Approved"       count={counts.approved || 0} testid="listing-count-approved"/>
                <StatusChip icon={AlertTriangle} tone="red"   label="Rejected"       count={counts.rejected || 0} testid="listing-count-rejected"/>
            </div>

            <Panel title={`Listings (${items.length})`} testid="partner-listings-table">
                {loading ? <div className="text-black/55 text-sm py-8">Loading…</div>
                    : items.length === 0 ? <EmptyState msg='No listings yet. Click "Add New Property" to submit your first listing.'/>
                    : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[800px]">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                                    <th className="pb-3 pr-4">Property</th>
                                    <th className="pb-3 pr-4">City</th>
                                    <th className="pb-3 pr-4">Type</th>
                                    <th className="pb-3 pr-4">Price</th>
                                    <th className="pb-3 pr-4">Status</th>
                                    <th className="pb-3 pr-4">Submitted</th>
                                    <th className="pb-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((p) => (
                                    <tr key={p.id} className="border-b border-black/8 last:border-b-0" data-testid={`partner-listing-row-${p.id}`}>
                                        <td className="py-4 pr-4">
                                            <div className="flex items-center gap-3">
                                                {p.cover_image
                                                    ? <img src={p.cover_image} alt="" className="w-14 h-14 object-cover border border-black/10" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                                                    : <div className="w-14 h-14 bg-black/5 border border-black/10 flex items-center justify-center text-black/30"><ImageIcon size={16}/></div>}
                                                <div>
                                                    <div className="font-bold text-[14px]">{p.title}</div>
                                                    <div className="text-black/50 text-[11px]">{p.locality}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4 pr-4 text-black/70 text-[13px]">{p.city}</td>
                                        <td className="py-4 pr-4 text-black/70 text-[13px]">{p.type} &middot; {LISTING_TYPES.find(l => l.k === p.listing_type)?.l || p.listing_type}</td>
                                        <td className="py-4 pr-4 font-bold text-[var(--ebg-gold)] text-[13px]">{p.price_display}</td>
                                        <td className="py-4 pr-4">
                                            <StatusBadge s={p.approval_status || "pending"}/>
                                            {p.approval_status === "rejected" && p.rejection_reason && (
                                                <div className="text-[10px] text-red-700 mt-1 max-w-[220px]" title={p.rejection_reason}>{p.rejection_reason}</div>
                                            )}
                                        </td>
                                        <td className="py-4 pr-4 text-black/50 text-[12px]">{p.created_at ? new Date(p.created_at).toLocaleDateString("en-IN") : "—"}</td>
                                        <td className="py-4 text-right whitespace-nowrap">
                                            {p.approval_status === "approved" ? (
                                                <a href={`/properties/${p.slug || p.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--ebg-gold)] hover:text-black" data-testid={`partner-listing-view-${p.id}`}>
                                                    <ExternalLink size={12}/> View
                                                </a>
                                            ) : (
                                                <>
                                                    <button onClick={() => setEditing(p)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] font-bold text-black hover:text-[var(--ebg-gold)] mr-3" data-testid={`partner-listing-edit-${p.id}`}>
                                                        <Edit2 size={12}/> Edit
                                                    </button>
                                                    <button onClick={() => remove(p)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] font-bold text-red-700 hover:text-black" data-testid={`partner-listing-delete-${p.id}`}>
                                                        <Trash2 size={12}/> Delete
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
        </div>
    );
}

/* ---------- Sub-components ---------- */

function StatusChip({ icon: Icon, tone, label, count, testid }) {
    const toneCls = tone === "green" ? "bg-emerald-50 border-emerald-300/70 text-emerald-800"
                  : tone === "red"   ? "bg-red-50 border-red-300/70 text-red-800"
                  :                    "bg-[var(--ebg-gold)]/10 border-[var(--ebg-gold)]/50 text-black";
    return (
        <div data-testid={testid} className={`border ${toneCls} px-4 py-3 flex items-center gap-3`}>
            <Icon size={18}/>
            <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-80">{label}</div>
                <div className="font-serif text-2xl leading-none mt-1">{count}</div>
            </div>
        </div>
    );
}

function StatusBadge({ s }) {
    if (s === "approved") return <Badge tone="green">Approved</Badge>;
    if (s === "rejected") return <Badge tone="red">Rejected</Badge>;
    return <Badge tone="gold">Pending Review</Badge>;
}

/* ---------- Add / Edit form ---------- */

function PartnerPropertyForm({ initial, onCancel, onSaved }) {
    const [busy, setBusy] = useState(false);
    // images[] is now stored as an array of absolute URLs (real uploads).
    const [f, setF] = useState(() => ({
        title: initial?.title || "",
        developer: initial?.developer || "",
        city: initial?.city || CITIES_ALL[0],
        locality: initial?.locality || "",
        country: initial?.country || "India",
        type: initial?.type || PROPERTY_TYPES[0],
        status: initial?.status || PROPERTY_STATUS[0],
        listing_type: initial?.listing_type || "sale",
        price: initial?.price ?? "",
        price_display: initial?.price_display || "",
        currency: initial?.currency || "INR",
        bhk: initial?.bhk ?? "",
        area_sqft: initial?.area_sqft ?? "",
        bathrooms: initial?.bathrooms ?? "",
        parking: initial?.parking ?? "",
        description: initial?.description || "",
        highlights: (initial?.highlights || []).join("\n"),
        amenities: (initial?.amenities || []).join("\n"),
        cover_image: initial?.cover_image || "",
        images: initial?.images || [],
        address: initial?.address || "",
        rera_number: initial?.rera_number || "",
        possession_date: initial?.possession_date || "",
    }));

    const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!f.title || !f.city || !f.locality || !f.description || !f.cover_image || !f.price_display) {
            toast.error("Title, City, Locality, Price display, Cover image and Description are required.");
            return;
        }
        const payload = {
            title: f.title.trim(),
            developer: f.developer.trim() || null,
            city: f.city,
            locality: f.locality.trim(),
            country: f.country,
            type: f.type,
            status: f.status,
            listing_type: f.listing_type,
            price: Number(f.price) || 0,
            price_display: f.price_display.trim(),
            currency: f.currency,
            bhk: f.bhk === "" ? null : Number(f.bhk),
            area_sqft: f.area_sqft === "" ? null : Number(f.area_sqft),
            bathrooms: f.bathrooms === "" ? null : Number(f.bathrooms),
            parking: f.parking === "" ? null : Number(f.parking),
            description: f.description.trim(),
            highlights: f.highlights.split("\n").map((s) => s.trim()).filter(Boolean),
            amenities:  f.amenities.split("\n").map((s) => s.trim()).filter(Boolean),
            cover_image: f.cover_image.trim(),
            images: (f.images || []).filter(Boolean),
            address: f.address.trim() || null,
            rera_number: f.rera_number.trim() || null,
            possession_date: f.possession_date.trim() || null,
        };
        setBusy(true);
        try {
            if (initial?.id) {
                await api.put(`/partner/properties/${initial.id}`, payload);
                toast.success("Listing updated and re-submitted for review");
            } else {
                await api.post("/partner/properties", payload);
                toast.success("Property submitted for review. You'll be notified on approval.");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="partner-property-form">
            <div className="flex items-center gap-4">
                <button onClick={onCancel} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-black/60 hover:text-black" data-testid="partner-form-back">
                    <ArrowLeft size={14}/> Back to Listings
                </button>
            </div>
            <div>
                <h1 className="font-serif text-3xl md:text-4xl">{initial?.id ? "Edit Property" : "Add New Property"}</h1>
                <p className="text-black/55 text-sm mt-2 max-w-2xl">
                    Fill in the details below. Every submission passes through a quick review (usually 1 business day) before going live.
                </p>
            </div>

            <form onSubmit={submit} className="bg-white border border-black/10 p-6 md:p-8 space-y-8">
                {/* Basics */}
                <Section title="1. Basics">
                    <Field l="Property Title *" v={f.title} onChange={(v) => set("title", v)} testid="pf-title" placeholder="e.g., 3 BHK Sea-View Apartment"/>
                    <Field l="Developer / Owner" v={f.developer} onChange={(v) => set("developer", v)} testid="pf-developer"/>
                    <Select l="City *" v={f.city} onChange={(v) => set("city", v)} testid="pf-city" options={CITIES_ALL}/>
                    <Field l="Locality *" v={f.locality} onChange={(v) => set("locality", v)} testid="pf-locality" placeholder="e.g., Bandra West"/>
                    <Select l="Property Type *" v={f.type} onChange={(v) => set("type", v)} testid="pf-type" options={PROPERTY_TYPES}/>
                    <Select l="Status" v={f.status} onChange={(v) => set("status", v)} testid="pf-status" options={PROPERTY_STATUS}/>
                    <Select l="Listing Type" v={f.listing_type} onChange={(v) => set("listing_type", v)} testid="pf-listing-type" options={LISTING_TYPES.map(l => l.k)} labels={LISTING_TYPES.reduce((a, l) => ({ ...a, [l.k]: l.l }), {})}/>
                </Section>

                {/* Pricing */}
                <Section title="2. Pricing & Configuration">
                    <Field l="Price (numeric — in ₹ Cr for India, AED M for UAE)" v={f.price} onChange={(v) => set("price", v)} testid="pf-price" type="number" placeholder="e.g., 3.5"/>
                    <Field l="Price Display *" v={f.price_display} onChange={(v) => set("price_display", v)} testid="pf-price-display" placeholder="e.g., ₹3.5 Cr onwards"/>
                    <Field l="BHK" v={f.bhk} onChange={(v) => set("bhk", v)} testid="pf-bhk" type="number"/>
                    <Field l="Area (sq.ft)" v={f.area_sqft} onChange={(v) => set("area_sqft", v)} testid="pf-area" type="number"/>
                    <Field l="Bathrooms" v={f.bathrooms} onChange={(v) => set("bathrooms", v)} testid="pf-bathrooms" type="number"/>
                    <Field l="Parking" v={f.parking} onChange={(v) => set("parking", v)} testid="pf-parking" type="number"/>
                </Section>

                {/* Description */}
                <Section title="3. Description & Details" cols={1}>
                    <Textarea l="Description *" v={f.description} onChange={(v) => set("description", v)} testid="pf-description" rows={5} placeholder="Detailed description of the property, key features, USPs…"/>
                    <Textarea l="Highlights (one per line)" v={f.highlights} onChange={(v) => set("highlights", v)} testid="pf-highlights" rows={4} placeholder={"e.g.,\nSea-facing apartment\nPremium marble flooring\nModular kitchen"}/>
                    <Textarea l="Amenities (one per line)" v={f.amenities} onChange={(v) => set("amenities", v)} testid="pf-amenities" rows={4} placeholder={"e.g.,\nSwimming pool\nGym\nClubhouse"}/>
                </Section>

                {/* Images */}
                <Section title="4. Images" cols={1}>
                    <ImageUploader
                        label="Cover Image *"
                        value={f.cover_image}
                        onChange={(v) => set("cover_image", v)}
                        testid="pf-cover-uploader"
                        hint="Recommended: landscape orientation, at least 1600×900px. JPG/PNG/WEBP up to 8 MB."
                    />
                    <GalleryUploader
                        label="Gallery Images (up to 20)"
                        values={f.images}
                        onChange={(v) => set("images", v)}
                        max={20}
                        testid="pf-gallery-uploader"
                    />
                </Section>

                {/* Optional */}
                <Section title="5. Additional (Optional)">
                    <Field l="Full Address" v={f.address} onChange={(v) => set("address", v)} testid="pf-address"/>
                    <Field l="RERA Number" v={f.rera_number} onChange={(v) => set("rera_number", v)} testid="pf-rera"/>
                    <Field l="Possession Date" v={f.possession_date} onChange={(v) => set("possession_date", v)} testid="pf-possession" placeholder="e.g., Dec 2027"/>
                </Section>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-black/10">
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="partner-form-submit"
                        className="inline-flex items-center justify-center gap-2 bg-[var(--ebg-gold)] text-black px-8 py-3.5 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] transition-colors disabled:opacity-60"
                    >
                        {busy ? "Submitting…" : (initial?.id ? "Save & Re-submit for Review" : "Submit for Admin Approval")}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="inline-flex items-center justify-center gap-2 border border-black/20 text-black px-6 py-3.5 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-white transition-colors"
                    >
                        <X size={14}/> Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}

function Section({ title, children, cols = 2 }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--ebg-gold)] mb-4">{title}</div>
            <div className={`grid gap-4 ${cols === 1 ? "" : "md:grid-cols-2"}`}>
                {children}
            </div>
        </div>
    );
}

function Field({ l, v, onChange, testid, type = "text", placeholder }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{l}</div>
            <input
                type={type}
                value={v}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)] bg-white"
            />
        </label>
    );
}

function Textarea({ l, v, onChange, testid, rows = 3, placeholder }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{l}</div>
            <textarea
                value={v}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                rows={rows}
                placeholder={placeholder}
                className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)] bg-white font-mono text-[13px] leading-relaxed"
            />
        </label>
    );
}

function Select({ l, v, onChange, testid, options, labels }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{l}</div>
            <select
                value={v}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)] bg-white cursor-pointer"
            >
                {options.map((o) => <option key={o} value={o}>{labels?.[o] || o}</option>)}
            </select>
        </label>
    );
}
